// src/components/dashboard/SolidGaugeComponent.tsx

import GaugeComponent from "react-gauge-component";
import { format } from "date-fns";

interface SolidGaugeComponentProps {
  config: any;
}

type Status = "NORMAL" | "ABNORMAL";
type SubArc = { limit: number; color: string; length?: number };

const EPS = 1e-3;

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

const formatGaugeValue = (value: number | null): string => {
  if (value === null) return "--";

  return Number(value.toFixed(3)).toString();
};

function evaluateSingleTrigger(
  value: number | null,
  threshold: number | null,
  operator: string | null
): Status {
  if (value === null || threshold === null || operator === null) return "NORMAL";

  switch (operator) {
    case ">":
      return value > threshold ? "ABNORMAL" : "NORMAL";
    case "<":
      return value < threshold ? "ABNORMAL" : "NORMAL";
    case ">=":
      return value >= threshold ? "ABNORMAL" : "NORMAL";
    case "<=":
      return value <= threshold ? "ABNORMAL" : "NORMAL";
    case "=":
      return value === threshold ? "ABNORMAL" : "NORMAL";
    case "!=":
      return value !== threshold ? "ABNORMAL" : "NORMAL";
    default:
      return "NORMAL";
  }
}

function evaluateStatus(
  value: number | null,
  lowThreshold: number | null,
  lowOperator: string | null,
  highThreshold: number | null,
  highOperator: string | null,
  fallbackThreshold: number | null,
  fallbackOperator: string | null
): Status {
  if (value === null) return "NORMAL";

  const hasLow = lowThreshold !== null && lowOperator !== null;
  const hasHigh = highThreshold !== null && highOperator !== null;

  if (hasLow) {
    const lowStatus = evaluateSingleTrigger(value, lowThreshold, lowOperator);
    if (lowStatus === "ABNORMAL") return "ABNORMAL";
  }

  if (hasHigh) {
    const highStatus = evaluateSingleTrigger(value, highThreshold, highOperator);
    if (highStatus === "ABNORMAL") return "ABNORMAL";
  }

  // backward compatibility for old configs that only have threshold/operator
  if (!hasLow && !hasHigh) {
    return evaluateSingleTrigger(value, fallbackThreshold, fallbackOperator);
  }

  return "NORMAL";
}

/** Ensures strictly increasing limits and last limit == max */
const sanitizeSubArcs = (arcs: SubArc[], min: number, max: number): SubArc[] => {
  if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) {
    return [{ limit: 1, color: "green" }];
  }

  const out: SubArc[] = [];
  let prev = min;

  for (let i = 0; i < arcs.length; i++) {
    const a = arcs[i];
    let lim = clamp(a.limit, min, max);

    if (lim <= prev) lim = prev + EPS;
    if (lim > max) lim = max;

    out.push({ ...a, limit: lim });
    prev = lim;
  }

  if (out.length) {
    out[out.length - 1].limit = max;
  }

  for (let i = 1; i < out.length; i++) {
    if (!(out[i].limit > out[i - 1].limit)) {
      return [{ limit: max, color: out[out.length - 1]?.color || "green" }];
    }
  }

  return out;
};

const buildArcs = (
  min: number,
  max: number,
  lowThreshold: number | null,
  lowOperator: string | null,
  highThreshold: number | null,
  highOperator: string | null,
  fallbackThreshold: number | null,
  fallbackOperator: string | null
): SubArc[] => {
  const range = max - min;

  const fallback: SubArc[] = [
    { limit: min + range * 0.3, color: "green" },
    { limit: min + range * 0.5, color: "yellow" },
    { limit: max, color: "red" },
  ];

  const hasLowVisual =
    lowThreshold !== null &&
    Number.isFinite(lowThreshold) &&
    (lowOperator === "<" || lowOperator === "<=");

  const hasHighVisual =
    highThreshold !== null &&
    Number.isFinite(highThreshold) &&
    (highOperator === ">" || highOperator === ">=");

  // preferred dual-trigger visual
  if (hasLowVisual && hasHighVisual) {
    const low = clamp(lowThreshold as number, min, max);
    const high = clamp(highThreshold as number, min, max);

    if (high <= low + EPS) {
      return [{ limit: max, color: "green" }];
    }

    return [
      { limit: low, color: "red" },
      { limit: high, color: "green" },
      { limit: max, color: "red" },
    ];
  }

  // low-only visual
  if (hasLowVisual) {
    const low = clamp(lowThreshold as number, min, max);
    const warn = Math.max(range * 0.1, EPS * 10);

    return [
      { limit: low, color: "red" },
      { limit: clamp(low + warn, min, max), color: "yellow" },
      { limit: max, color: "green" },
    ];
  }

  // high-only visual
  if (hasHighVisual) {
    const high = clamp(highThreshold as number, min, max);
    const warn = Math.max(range * 0.1, EPS * 10);

    return [
      { limit: clamp(high - warn, min, max), color: "green" },
      { limit: high, color: "yellow" },
      { limit: max, color: "red" },
    ];
  }

  // backward compatibility with old single-trigger setup
  if (
    fallbackThreshold !== null &&
    Number.isFinite(fallbackThreshold) &&
    fallbackOperator
  ) {
    const t = clamp(fallbackThreshold, min, max);
    const warn = Math.max(range * 0.1, EPS * 10);

    if (fallbackOperator === "<" || fallbackOperator === "<=") {
      return [
        { limit: t, color: "red" },
        { limit: clamp(t + warn, min, max), color: "yellow" },
        { limit: max, color: "green" },
      ];
    }

    if (fallbackOperator === ">" || fallbackOperator === ">=") {
      return [
        { limit: clamp(t - warn, min, max), color: "green" },
        { limit: t, color: "yellow" },
        { limit: max, color: "red" },
      ];
    }
  }

  return fallback;
};

export function SolidGaugeComponent({ config }: SolidGaugeComponentProps) {
  const unit = config.unit || "";
  const value = config.value !== undefined && !isNaN(config.value) ? config.value : null;
  const timestamp = config.timestamp;

  let min = typeof config.min === "number" ? config.min : 0;
  let max = typeof config.max === "number" ? config.max : 100;

  if (!Number.isFinite(min) || !Number.isFinite(max) || min >= max) {
    console.warn(`⚠️ Invalid gauge range: min=${min}, max=${max}. Falling back to 0–1000.`);
    min = 0;
    max = 1000;
  }

  if (max - min < 0.01) {
    max = min + 1;
  }

  // new dual-trigger fields
  const lowThreshold = config.low_threshold ?? null;
  const lowOperator = config.low_operator ?? null;
  const highThreshold = config.high_threshold ?? null;
  const highOperator = config.high_operator ?? null;

  // old single-trigger fallback
  const threshold = config.threshold ?? null;
  const operator = config.operator ?? null;

  const status = evaluateStatus(
    value,
    lowThreshold,
    lowOperator,
    highThreshold,
    highOperator,
    threshold,
    operator
  );

  const rawSubArcs = buildArcs(
    min,
    max,
    lowThreshold,
    lowOperator,
    highThreshold,
    highOperator,
    threshold,
    operator
  );

  const safeSubArcs = sanitizeSubArcs(rawSubArcs, min, max);

  const tickInterval = (max - min) / 5;
  const ticks =
    Number.isFinite(tickInterval) && tickInterval > 0
      ? Array.from({ length: 6 }, (_, i) => min + i * tickInterval)
      : [min, max];


  return (
  <div
    style={{
      backgroundColor: "#001f3f",
      color: "#ffffff",
      width: "100%",
      height: "100%",
      minWidth: 0,
      minHeight: 0,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    }}
  >
    {/* Gauge area - takes remaining available height */}
    <div
      style={{
        flex: "1 1 0",
        minWidth: 0,
        minHeight: 0,
        width: "100%",
        overflow: "hidden",
      }}
    >
      <GaugeComponent
        style={{
          width: "100%",
          height: "100%",
        }}
        arc={{
          subArcs: safeSubArcs,
        }}
        labels={{
          valueLabel: {
            formatTextValue: () => formatGaugeValue(value),
            style: {
              fill: "#ffffff",
              fontSize: "20px",
            },
          },
          tickLabels: {
            type: "outer",
            ticks: ticks.map((v) => ({
              value: v,
              valueConfig: {
                formatTextValue: (val: number) => `${val}`,
                style: {
                  fill: "#ffffff",
                  fontSize: "10px",
                },
              },
            })),
            defaultTickValueConfig: {
              formatTextValue: (val: number) => `${val}${unit}`,
            },
          },
        }}
        value={value ?? 0}
        minValue={min}
        maxValue={max}
      />
    </div>

    {/* Fixed footer - always visible */}
    <div
      style={{
        flex: "0 0 22px",
        height: "22px",
        minHeight: "22px",
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "10px",
        fontWeight: 500,
        color: status === "ABNORMAL" ? "white" : "lightgreen",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        padding: "0 4px",
      }}
      title={
        timestamp
          ? `${status} | ${format(new Date(timestamp), "yyyy-MM-dd HH:mm:ss")}`
          : status
      }
    >
      {status} |{" "}
      {timestamp
        ? format(new Date(timestamp), "yyyy-MM-dd HH:mm:ss")
        : "--"}
    </div>
  </div>
);

  
  // return (
  //   <div
  //     style={{
  //       backgroundColor: "#001f3f",
  //       borderRadius: "0px",
  //       color: "#ffffff",
  //       width: "100%",
  //       height: "100%",
  //       display: "flex",
  //       flexDirection: "column",
  //       alignItems: "center",
  //       justifyContent: "center",
  //     }}
  //   >
  //     <div
  //       style={{
  //         width: "100%",
  //         height: "180px",
  //         display: "flex",
  //         flexDirection: "column",
  //         alignItems: "center",
  //         justifyContent: "center",
  //         margin: "0 auto",
  //       }}
  //     >
  //       <GaugeComponent
  //         arc={{
  //           subArcs: safeSubArcs,
  //         }}
  //         labels={{
  //           valueLabel: {
  //             formatTextValue: () => formatGaugeValue(value),
  //             style: { fill: "#ffffff", fontSize: "20rem" },
  //           },
  //           tickLabels: {
  //             type: "outer",
  //             ticks: ticks.map((v) => ({
  //               value: v,
  //               valueConfig: {
  //                 formatTextValue: (val) => `${val}`,
  //                 style: { fill: "#ffffff", fontSize: "0.6em" },
  //               },
  //             })),
  //             defaultTickValueConfig: {
  //               formatTextValue: (val) => `${val}${unit}`,
  //             },
  //           },
  //         }}
  //         value={value ?? 0}
  //         minValue={min}
  //         maxValue={max}
  //       />

  //       <div
  //         className="text-center"
  //         style={{
  //           fontSize: "0.6em",
  //           fontWeight: 500,
  //           color: status === "ABNORMAL" ? "white" : "lightgreen",
  //           marginTop: "0rem",
  //         }}
  //       >
  //         {status} | {timestamp ? format(new Date(timestamp), "yyyy-MM-dd HH:mm:ss") : "--"}
  //       </div>
  //     </div>
  //   </div>
  // );
}

export default SolidGaugeComponent;